/**
 * 🚀 [Phase v13.47.1 — 2026-07-18] Linkify léger pour les messages de conversation.
 *
 * Objectif : détecter les URLs / emails dans un message texte plain (multi-lignes
 * conservés) et retourner un tableau de « tokens » React que le rendu du message
 * peut mapper — SANS `dangerouslySetInnerHTML` (safe by design).
 *
 * Volontairement PAS de library rich-text : regex maison SANS backtracking
 * catastrophique. Couverture :
 *   • `http(s)://…`
 *   • `www.…`     (préfixe `https://` ajouté au clic)
 *   • `email@domaine.tld` (mailto)
 *
 * Les retours ligne `\n` sont préservés à part par le composant appelant via
 * `white-space: pre-wrap`. Cette fonction ne les touche PAS.
 */

// Regex safe (une seule alternance, quantifieurs bornés, pas de backreferences).
//   - `\bhttps?://[^\s<]+`   → URLs explicites
//   - `\bwww\.[^\s<]+`       → URLs sans schéma
//   - `\b[\w.-]+@[\w.-]+\.\w{2,}\b` → emails
const LINK_RE = /(\bhttps?:\/\/[^\s<]+|\bwww\.[^\s<]+|\b[\w.+-]+@[\w.-]+\.\w{2,}\b)/g;

// Ponctuation de fin fréquemment collée à une URL et à retirer visuellement de
// la partie « lien » (mais préservée à côté). Exemple :
//   « Regarde https://exemple.com/page. Et aussi… » → lien = `https://exemple.com/page`
//   trailing = `.`
const TRAILING_PUNCT = /[.,;:!?)\]}»"']+$/;

/**
 * @typedef {{type: 'text' | 'url' | 'email', content: string, href?: string}} Token
 */

/**
 * Découpe un texte brut en tokens texte / url / email.
 * @param {string} text
 * @returns {Token[]}
 */
export function linkifyMessage(text) {
  if (!text || typeof text !== 'string') return [];
  const tokens = [];
  let lastIndex = 0;
  // Reset le lastIndex de la regex globale (sécurité en cas de réutilisation)
  LINK_RE.lastIndex = 0;
  let match;
  while ((match = LINK_RE.exec(text)) !== null) {
    const raw = match[0];
    const start = match.index;
    // Push le texte précédent
    if (start > lastIndex) {
      tokens.push({ type: 'text', content: text.slice(lastIndex, start) });
    }
    // Isole une éventuelle ponctuation finale collée
    let clean = raw;
    let trailing = '';
    const trailingMatch = clean.match(TRAILING_PUNCT);
    if (trailingMatch) {
      trailing = trailingMatch[0];
      clean = clean.slice(0, -trailing.length);
    }
    // Classe le token
    if (clean.includes('@') && !clean.startsWith('http') && !clean.startsWith('www.')) {
      tokens.push({ type: 'email', content: clean, href: `mailto:${clean}` });
    } else {
      const href = clean.startsWith('http') ? clean : `https://${clean}`;
      tokens.push({ type: 'url', content: clean, href });
    }
    if (trailing) {
      tokens.push({ type: 'text', content: trailing });
    }
    lastIndex = start + raw.length;
  }
  // Reste du texte après le dernier match
  if (lastIndex < text.length) {
    tokens.push({ type: 'text', content: text.slice(lastIndex) });
  }
  return tokens;
}
